import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { toDate } from './progress';
import { isAutomationBrowser } from './isAutomationBrowser';

/** CFA-26: counters go through Admin SDK (Netlify), not open client writes. */
async function postSiteStat(body) {
  if (typeof window === 'undefined') return;
  try {
    await fetch('/.netlify/functions/record-site-stat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    /* non-blocking analytics */
  }
}

export const DAY_MS = 24 * 60 * 60 * 1000;
export const DAU_MS = DAY_MS;
export const WAU_MS = 7 * DAY_MS;
export const MAU_MS = 30 * DAY_MS;
export const STATS_DAYS = 30;

export function utcDay(value = new Date()) {
  const date = toDate(value) || (value instanceof Date ? value : null);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function lastNDays(n = STATS_DAYS) {
  const days = [];
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  for (let i = n - 1; i >= 0; i -= 1) {
    days.push(new Date(Date.UTC(y, m, d - i)).toISOString().slice(0, 10));
  }
  return days;
}

export function pathField(pathname) {
  const raw = String(pathname || '/')
    .split('?')[0]
    .replace(/\/+$/, '') || '/';
  return raw.slice(0, 80).replace(/\./g, '_').replace(/\//g, '~');
}

export function fieldPath(field) {
  return String(field || '~').replace(/~/g, '/') || '/';
}

function visitorSeenKey(day) {
  return `cfa_visit_${day}`;
}

/**
 * Rebuild nested funnel/paths/levels from literal dotted field keys
 * (`funnel.event`, `paths.~slug`, `levels.l1`) used by record-site-stat.
 */
export function normalizeSiteStatsDoc(day, data = {}) {
  const funnel =
    data.funnel && typeof data.funnel === 'object' && !Array.isArray(data.funnel)
      ? { ...data.funnel }
      : {};
  const paths =
    data.paths && typeof data.paths === 'object' && !Array.isArray(data.paths)
      ? { ...data.paths }
      : {};
  const levels =
    data.levels && typeof data.levels === 'object' && !Array.isArray(data.levels)
      ? { ...data.levels }
      : {};
  Object.entries(data).forEach(([key, value]) => {
    if (key.startsWith('funnel.') && key.length > 7) {
      funnel[key.slice(7)] = Number(value) || 0;
    } else if (key.startsWith('paths.') && key.length > 6) {
      paths[key.slice(6)] = Number(value) || 0;
    } else if (key.startsWith('levels.') && key.length > 7) {
      levels[key.slice(7)] = Number(value) || 0;
    }
  });
  return {
    day,
    pageviews: Number(data.pageviews) || 0,
    visitors: Number(data.visitors) || 0,
    paths,
    funnel,
    levels,
  };
}

export async function recordPageView(pathname, { level = null } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (document.visibilityState === 'hidden') return;
  if (isAutomationBrowser()) return;
  const path = String(pathname || window.location.pathname || '/');
  if (path.startsWith('/admin')) return;

  const day = utcDay(new Date());
  if (!day) return;
  let visitor = false;
  try {
    if (window.localStorage.getItem(visitorSeenKey(day)) !== '1') {
      visitor = true;
      window.localStorage.setItem(visitorSeenKey(day), '1');
    }
  } catch {
    visitor = true;
  }
  const lvl = String(level || '')
    .trim()
    .toLowerCase();
  await postSiteStat({
    kind: 'pageview',
    day,
    path,
    visitor,
    level: lvl === 'l1' || lvl === 'l2' || lvl === 'l3' ? lvl : '',
  });
}

async function loadSiteStatsViaAdmin(days, idToken) {
  const res = await fetch(`/.netlify/functions/admin-site-stats?days=${encodeURIComponent(days)}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.error || `admin-site-stats ${res.status}`);
  }
  const rows = Array.isArray(body.days) ? body.days : [];
  if (!rows.length) {
    throw new Error('admin-site-stats returned no days');
  }
  return rows.map((row) => ({
    day: row.day,
    pageviews: Number(row.pageviews) || 0,
    visitors: Number(row.visitors) || 0,
    paths: row.paths && typeof row.paths === 'object' ? row.paths : {},
    funnel: row.funnel && typeof row.funnel === 'object' ? row.funnel : {},
    levels: row.levels && typeof row.levels === 'object' ? row.levels : {},
  }));
}

async function loadSiteStatsViaClient(days) {
  const keys = lastNDays(days);
  // Prefer per-day gets (CFA-29): full-collection list has been failing silently in admin
  // and zeroing traffic KPIs even when siteStats docs exist.
  try {
    const snaps = await Promise.all(keys.map((day) => getDoc(doc(db, 'siteStats', day))));
    return keys.map((day, index) => {
      const snap = snaps[index];
      return normalizeSiteStatsDoc(day, snap.exists() ? snap.data() || {} : {});
    });
  } catch (perDayError) {
    // Fallback: collection list (older path).
    const snap = await getDocs(collection(db, 'siteStats'));
    const byDay = {};
    snap.docs.forEach((item) => {
      if (!keys.includes(item.id)) return;
      byDay[item.id] = normalizeSiteStatsDoc(item.id, item.data() || {});
    });
    if (!Object.keys(byDay).length && perDayError) throw perDayError;
    return keys.map(
      (day) => byDay[day] || { day, pageviews: 0, visitors: 0, paths: {}, funnel: {}, levels: {} }
    );
  }
}

/**
 * Load last N UTC days of siteStats.
 * @param {number|{days?: number, idToken?: string}} daysOrOpts
 */
export async function loadSiteStatsRange(daysOrOpts = STATS_DAYS) {
  const opts =
    daysOrOpts && typeof daysOrOpts === 'object' ? daysOrOpts : { days: daysOrOpts };
  const days = Number(opts.days) > 0 ? Number(opts.days) : STATS_DAYS;
  const idToken = String(opts.idToken || '').trim();

  if (idToken && typeof window !== 'undefined') {
    try {
      return await loadSiteStatsViaAdmin(days, idToken);
    } catch (adminError) {
      // Fall through to client reads; AdminPage surfaces if both fail.
      console.warn('admin-site-stats failed, trying client Firestore', adminError?.message || adminError);
    }
  }

  return loadSiteStatsViaClient(days);
}

export async function recordFunnelEvent(eventName) {
  if (typeof window === 'undefined' || !eventName) return;
  if (isAutomationBrowser()) return;
  const day = utcDay(new Date());
  if (!day) return;
  await postSiteStat({ kind: 'funnel', day, event: String(eventName).slice(0, 80) });
}

export function sumFunnelSeries(rows = [], eventName, n = 7) {
  const slice = rows.slice(-n);
  return slice.reduce((total, row) => total + (Number(row.funnel?.[eventName]) || 0), 0);
}

export function sumFunnelPrefix(rows = [], prefix, n = 7) {
  const slice = rows.slice(-n);
  return slice.reduce((total, row) => {
    const funnel = row.funnel && typeof row.funnel === 'object' ? row.funnel : {};
    return (
      total +
      Object.entries(funnel).reduce(
        (subtotal, [key, value]) =>
          key.startsWith(prefix) ? subtotal + (Number(value) || 0) : subtotal,
        0
      )
    );
  }, 0);
}

export function sumFunnelEvents(rows = [], eventNames = [], n = 7) {
  return eventNames.reduce((total, eventName) => total + sumFunnelSeries(rows, eventName, n), 0);
}

export function countByDay(values, days = STATS_DAYS) {
  const keys = lastNDays(days);
  const counts = Object.fromEntries(keys.map((day) => [day, 0]));
  values.forEach((value) => {
    const day = utcDay(value);
    if (day && counts[day] != null) counts[day] += 1;
  });
  return keys.map((day) => ({ day, value: counts[day] }));
}

export function sumSeries(series, n) {
  const slice = series.slice(-n);
  return slice.reduce((total, row) => total + (Number(row.value) || Number(row.pageviews) || 0), 0);
}

export function topPaths(rows = [], limit = 6) {
  const counts = {};
  rows.forEach((row) => {
    Object.entries(row.paths || {}).forEach(([key, value]) => {
      const path = fieldPath(key);
      counts[path] = (counts[path] || 0) + (Number(value) || 0);
    });
  });
  return Object.entries(counts)
    .map(([path, count]) => ({ path, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

/** Aggregate pageviews attributed to CFA level intent (l1/l2/l3). */
export function levelTraffic(rows = [], n = 7) {
  const slice = rows.slice(-n);
  const totals = { l1: 0, l2: 0, l3: 0 };
  slice.forEach((row) => {
    const levels = row.levels && typeof row.levels === 'object' ? row.levels : {};
    totals.l1 += Number(levels.l1) || 0;
    totals.l2 += Number(levels.l2) || 0;
    totals.l3 += Number(levels.l3) || 0;
  });
  return totals;
}

export function accountTraffic(users = [], now = Date.now()) {
  const seen = (user) => user.lastSeenAt || user.lastActiveAt || user.lastSignInAt;
  const dau = users.filter((user) => seen(user) && now - seen(user).getTime() <= DAU_MS).length;
  const wau = users.filter((user) => seen(user) && now - seen(user).getTime() <= WAU_MS).length;
  const mau = users.filter((user) => seen(user) && now - seen(user).getTime() <= MAU_MS).length;
  const new7 = users.filter((user) => user.createdAt && now - user.createdAt.getTime() <= WAU_MS).length;
  const new30 = users.filter((user) => user.createdAt && now - user.createdAt.getTime() <= MAU_MS).length;
  return { dau, wau, mau, new7, new30 };
}
